import { useEffect } from 'react';
import { toast } from 'sonner';
import { useAuthStore } from '@stores/useAuthStore';
import { useShallow } from 'zustand/react/shallow';
import {
  broadcastOAuthConnectionEvent,
  getCommunityIdFromOAuthReturnPath,
} from '@lib/oauthConnectionEvents';

export const DISCORD_COMMUNITY_SELECTION_STORAGE_KEY =
  'bbb_pending_discord_community_selection';

export function useDiscordCallbackEffect() {
  const { isAuthenticated, refreshUserProfile } = useAuthStore(useShallow(s => ({
    isAuthenticated: s.isAuthenticated,
    refreshUserProfile: s.refreshUserProfile,
  })));

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const status = params.get('discord_status');
    if (!status) {
      return;
    }

    const username = params.get('discord_username');
    const reason = params.get('discord_reason');
    const selectionToken = params.get('discord_selection_token');
    const communityId = getCommunityIdFromOAuthReturnPath(window.location.pathname);

    broadcastOAuthConnectionEvent({
      provider: 'discord',
      status:
        status === 'success'
          ? 'success'
          : status === 'server_selection_required'
            ? 'server_selection_required'
            : 'error',
      communityId,
      username: username ?? undefined,
      reason: reason ?? undefined,
      selectionToken: selectionToken ?? undefined,
    });

    const cleanParams = () => {
      params.delete('discord_status');
      params.delete('discord_username');
      params.delete('discord_reason');
      params.delete('discord_selection_token');

      const remaining = params.toString();
      const cleanUrl = remaining
        ? `${window.location.pathname}?${remaining}`
        : window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    };

    (async () => {
      if (status === 'success') {
        if (!isAuthenticated) {
          cleanParams();
          return;
        }

        try {
          await refreshUserProfile();
          const description = username ? `Account linked as ${username}` : undefined;
          toast.success('Connected to Discord', { description });
        } catch (error) {
          console.error('Failed to refresh profile after Discord connect', error);
        }
      } else if (status === 'server_selection_required') {
        if (selectionToken) {
          window.sessionStorage.setItem(
            DISCORD_COMMUNITY_SELECTION_STORAGE_KEY,
            selectionToken,
          );
        }
        toast.info('Select your official Discord server to finish connecting.', {
          description: username
            ? `Connected as ${username}. You can return to the original tab.`
            : 'You can return to the original tab.',
        });
      } else {
        const isAuthFailure = reason === 'auth_failed';
        const description = isAuthFailure ? undefined : (reason || 'Please try connecting again.');
        const message = isAuthFailure ? 'Discord connection failed! try again!' : 'Discord connection failed';
        toast.error(message, { description });
      }

      cleanParams();
    })();
  }, [isAuthenticated, refreshUserProfile]);
}
